/**
 * Performance optimization utilities for VegaDigital marketing website
 * Ensures < 2s page loads as per Constitution requirements
 */

import React from 'react';

/**
 * Performance monitoring hooks
 */
export const usePagePerformance = (pageName: string) => {
  React.useEffect(() => {
    // Monitor Core Web Vitals
    if (typeof window === 'undefined') return;

    const measurePerformance = () => {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      
      if (navigation) {
        const metrics = {
          // First Contentful Paint
          fcp: navigation.domContentLoadedEventEnd - navigation.fetchStart,
          // Largest Contentful Paint (estimated)
          lcp: navigation.loadEventEnd - navigation.fetchStart,
          // Time to Interactive (estimated)
          tti: navigation.domInteractive - navigation.fetchStart,
          // Total page load time
          totalTime: navigation.loadEventEnd - navigation.fetchStart,
        };

        // Log performance metrics
        console.log(`[Performance] ${pageName}:`, metrics);

        // Send to analytics if available
        if ((window as any).gtag) {
          (window as any).gtag('event', 'page_performance', {
            page_name: pageName,
            fcp: Math.round(metrics.fcp),
            lcp: Math.round(metrics.lcp),
            tti: Math.round(metrics.tti),
            total_time: Math.round(metrics.totalTime),
          });
        }

        // Alert if performance targets not met
        if (metrics.totalTime > 2000) {
          console.warn(`[Performance] Page ${pageName} exceeded 2s load time: ${Math.round(metrics.totalTime)}ms`);
        }
      }
    };

    // Measure after page is fully loaded
    if (document.readyState === 'complete') {
      setTimeout(measurePerformance, 100);
    } else {
      window.addEventListener('load', () => setTimeout(measurePerformance, 100));
    }
  }, [pageName]);
};

/**
 * Resource preloading utilities
 */
export const preloadCriticalResources = () => {
  if (typeof document === 'undefined') return;

  // Preload critical fonts
  const fontPreloads = [
    '/fonts/Inter-Regular.woff2',
    '/fonts/Inter-SemiBold.woff2',
    '/fonts/Inter-Bold.woff2',
  ];

  fontPreloads.forEach((href) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = href;
    link.as = 'font';
    link.type = 'font/woff2';
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  });

  // Preload critical images
  const imagePreloads = [
    '/images/hero-background.webp',
    '/images/logo.svg',
  ];

  imagePreloads.forEach((href) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = href;
    link.as = 'image';
    document.head.appendChild(link);
  });
};

/**
 * Service Worker registration for caching
 */
export const registerServiceWorker = () => {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        console.log('[SW] Service Worker registered successfully:', registration.scope);
      })
      .catch((error) => {
        console.log('[SW] Service Worker registration failed:', error);
      });
  });
};

/**
 * Critical CSS inlining utility
 */
export const inlineCriticalCSS = () => {
  const criticalCSS = `
    /* Critical above-the-fold styles */
    body {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      margin: 0;
      padding: 0;
      line-height: 1.6;
    }
    
    .hero-section {
      min-height: 60vh;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .navigation {
      position: sticky;
      top: 0;
      z-index: 1000;
      background: rgba(255, 255, 255, 0.95);
      backdrop-filter: blur(8px);
    }
  `;

  if (typeof document !== 'undefined') {
    const style = document.createElement('style');
    style.textContent = criticalCSS;
    document.head.appendChild(style);
  }
};

/**
 * Performance budget monitoring
 */
export class PerformanceBudget {
  private static thresholds = {
    // Constitution requirement: < 2s page loads
    pageLoad: 2000,
    // API calls should be < 500ms
    apiResponse: 500,
    // First Contentful Paint
    fcp: 1500,
    // Largest Contentful Paint
    lcp: 2500,
    // Time to Interactive
    tti: 3000,
  };

  static checkPageLoadBudget(loadTime: number, pageName: string): boolean {
    const withinBudget = loadTime <= this.thresholds.pageLoad;
    
    if (!withinBudget) {
      console.warn(
        `[Performance Budget] Page ${pageName} exceeded budget: ${Math.round(loadTime)}ms > ${this.thresholds.pageLoad}ms`
      );
      
      // Send alert to monitoring system
      if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('event', 'performance_budget_exceeded', {
          page_name: pageName,
          load_time: Math.round(loadTime),
          budget: this.thresholds.pageLoad,
          metric: 'page_load',
        });
      }
    }

    return withinBudget;
  }

  static checkApiBudget(responseTime: number, endpoint: string): boolean {
    const withinBudget = responseTime <= this.thresholds.apiResponse;
    
    if (!withinBudget) {
      console.warn(
        `[API Budget] ${endpoint} exceeded budget: ${Math.round(responseTime)}ms > ${this.thresholds.apiResponse}ms`
      );
    }

    return withinBudget;
  }
}

/**
 * Resource hints for better performance
 */
export const addResourceHints = () => {
  if (typeof document === 'undefined') return;

  // DNS prefetch for external domains
  const dnsPrefetches = [
    'fonts.googleapis.com',
    'fonts.gstatic.com',
    'www.google-analytics.com',
  ];

  dnsPrefetches.forEach((domain) => {
    const link = document.createElement('link');
    link.rel = 'dns-prefetch';
    link.href = `//${domain}`;
    document.head.appendChild(link);
  });

  // Preconnect to critical external origins
  const preconnects = [
    'https://fonts.googleapis.com',
    'https://fonts.gstatic.com',
  ];

  preconnects.forEach((origin) => {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = origin;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  });
};

// Initialize performance optimizations
export const initializePerformanceOptimizations = () => {
  if (typeof window === 'undefined') return;

  // Add resource hints
  addResourceHints();
  
  // Preload critical resources
  preloadCriticalResources();
  
  // Inline critical CSS
  inlineCriticalCSS();
  
  // Register service worker in production
  if (process.env.NODE_ENV === 'production') {
    registerServiceWorker();
  }
};

export default {
  usePagePerformance,
  PerformanceBudget,
  initializePerformanceOptimizations,
};